import Link from 'next/link';
import { ComponentProps } from 'react';
import {
  getArticleLink,
  getAudioLink,
  getTVShowLink,
  getVideoLink,
  QueryParams,
} from './link-utils';
import { Content, ContentMetadata } from '@/lib/model';
import {
  isAudioContentMetadataType,
  isVideoContentMetadataType,
} from '@/utils/contentType/contentType';
import { cn } from '@/lib/utils';
import { LinkWithChannelLink } from './LinkWithChannel';

interface MediaLinkProps
  extends Omit<ComponentProps<typeof Link>, 'href' | 'content'> {
  contentMetadata?: ContentMetadata;
  subType?: Content['subType'];
  href: string;
}

interface Props extends Omit<MediaLinkProps, 'content' | 'href'> {
  mobileContentMetadata?: ContentMetadata;
  desktopContentMetadata?: ContentMetadata;
  containerClassName?: string;
  dataTestId?: string;
  queryParams?: QueryParams;
  onClick?: () => void;
  redirectUrl?: string;
}

export function getContentRoute(
  contentMetadata?: ContentMetadata,
  subType?: Content['subType'],
  queryParams?: QueryParams,
) {
  if (!contentMetadata) {
    return '';
  }
  return contentMetadata?.tvShow?.id
    ? getTVShowLink(
        contentMetadata.tvShow.id,
        contentMetadata?.slug as string,
        queryParams,
      )
    : isAudioContentMetadataType(contentMetadata)
      ? getAudioLink(contentMetadata.slug as string, queryParams)
      : isVideoContentMetadataType(contentMetadata)
        ? getVideoLink(contentMetadata.slug as string, subType, queryParams)
        : getArticleLink(contentMetadata.slug as string, queryParams);
}

const MediaLink = ({ href, contentMetadata, ...props }: MediaLinkProps) => {
  if (!contentMetadata?.slug) {
    return (
      <div style={props.style} className={props.className}>
        {props.children}
      </div>
    );
  }
  return <LinkWithChannelLink {...props} href={href} />;
};

export const ContentRouter = ({
  mobileContentMetadata,
  desktopContentMetadata,
  className,
  containerClassName,
  subType,
  dataTestId,
  queryParams,
  onClick,
  redirectUrl,
  ...props
}: Props) => {
  if (redirectUrl) {
    return (
      <div
        className={containerClassName}
        onClick={onClick}
        data-testid={dataTestId}
      >
        <MediaLink
          className={className}
          {...props}
          contentMetadata={desktopContentMetadata}
          href={redirectUrl}
        />
      </div>
    );
  }
  const mobileHref = getContentRoute(
    mobileContentMetadata,
    subType,
    queryParams,
  );

  const desktopHref = getContentRoute(
    desktopContentMetadata,
    subType,
    queryParams,
  );

  if (mobileHref === desktopHref) {
    return (
      <div
        className={containerClassName}
        onClick={onClick}
        data-testid={dataTestId}
      >
        <MediaLink
          className={className}
          {...props}
          contentMetadata={desktopContentMetadata}
          href={desktopHref}
        />
      </div>
    );
  }

  return (
    <>
      <div
        className={cn('block lg:hidden', containerClassName)}
        data-testid={dataTestId}
        onClick={onClick}
      >
        <MediaLink
          className={className}
          {...props}
          contentMetadata={mobileContentMetadata}
          href={mobileHref}
        />
      </div>
      <div
        className={cn('hidden lg:block', containerClassName)}
        data-testid={dataTestId}
        onClick={onClick}
      >
        <MediaLink
          className={className}
          {...props}
          contentMetadata={desktopContentMetadata}
          href={desktopHref}
        />
      </div>
    </>
  );
};
